import { useState } from "react";
import { KiiAutoSuggestFilter, FilterOption, FilterColorVariant } from "./kii-auto-suggest-filter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FilterConfig<T = unknown> {
  id: string;
  label: string;
  placeholder?: string;
  searchPlaceholder?: string;
  colorVariant: FilterColorVariant;
  multiple?: boolean;
  options?: FilterOption<T>[];
  onSearch?: (query: string) => Promise<FilterOption<T>[]> | FilterOption<T>[];
  renderOption?: (option: FilterOption<T>) => React.ReactNode;
  renderSelected?: (option: FilterOption<T>) => React.ReactNode;
}

export interface FilterValues {
  [filterId: string]: string | string[] | null;
}

/**
 * KiiFilterPanel - Self-contained multi-filter panel with search
 *
 * A comprehensive filtering solution that manages:
 * - Multiple KiiAutoSuggestFilter components
 * - Optional search box
 * - Clear All functionality
 * - Both controlled and uncontrolled modes
 */
interface KiiFilterPanelProps {
  filters: FilterConfig[];
  showSearchBox?: boolean;
  searchPlaceholder?: string;
  onSearch?: (searchQuery: string, filterValues: FilterValues) => void;
  onFilterChange?: (filterValues: FilterValues) => void;
  onSearchQueryChange?: (searchQuery: string) => void;
  className?: string;
  layout?: "grid" | "vertical";
  searchBoxPosition?: "top" | "bottom";
  filterValues?: FilterValues;
  searchQuery?: string;
}

export function KiiFilterPanel({
  filters,
  showSearchBox = true,
  searchPlaceholder = "Search...",
  onSearch,
  onFilterChange,
  onSearchQueryChange,
  className,
  layout = "grid",
  searchBoxPosition = "top",
  filterValues: controlledFilterValues,
  searchQuery: controlledSearchQuery,
}: KiiFilterPanelProps) {
  const [internalFilterValues, setInternalFilterValues] = useState<FilterValues>(() => {
    const initial: FilterValues = {};
    filters.forEach(filter => {
      initial[filter.id] = filter.multiple ? [] : null;
    });
    return initial;
  });

  const [internalSearchQuery, setInternalSearchQuery] = useState("");

  const filterValues = controlledFilterValues !== undefined ? controlledFilterValues : internalFilterValues;
  const searchQuery = controlledSearchQuery !== undefined ? controlledSearchQuery : internalSearchQuery;

  const handleFilterChange = (filterId: string, value: string | string[] | null) => {
    const newValues = {
      ...filterValues,
      [filterId]: value,
    };
    if (controlledFilterValues === undefined) {
      setInternalFilterValues(newValues);
    }
    onFilterChange?.(newValues);
  };

  const handleClearAll = () => {
    const clearedValues: FilterValues = {};
    filters.forEach(filter => {
      clearedValues[filter.id] = filter.multiple ? [] : null;
    });
    if (controlledFilterValues === undefined) {
      setInternalFilterValues(clearedValues);
    }
    if (controlledSearchQuery === undefined) {
      setInternalSearchQuery("");
    }
    onSearchQueryChange?.("");
    onFilterChange?.(clearedValues);
    onSearch?.("", clearedValues);
  };

  const handleSearch = () => {
    onSearch?.(searchQuery, filterValues);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const hasAnyFilters = Object.values(filterValues).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== "";
  });

  const hasSearchQuery = searchQuery.trim().length > 0;
  const hasClearableContent = hasAnyFilters || hasSearchQuery;

  const searchBox = showSearchBox && (
    <div className="flex gap-2 w-full">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => {
            const newValue = e.target.value;
            if (controlledSearchQuery === undefined) {
              setInternalSearchQuery(newValue);
            }
            onSearchQueryChange?.(newValue);
          }}
          onKeyDown={handleSearchKeyDown}
          className="pl-9"
          data-testid="filter-panel-search"
        />
      </div>
      <Button
        onClick={handleSearch}
        data-testid="filter-panel-search-btn"
      >
        <Search className="h-4 w-4 mr-2" />
        Search
      </Button>
      {hasClearableContent && (
        <Button
          variant="outline"
          onClick={handleClearAll}
          data-testid="filter-panel-clear-all"
        >
          <X className="h-4 w-4 mr-2" />
          Clear All
        </Button>
      )}
    </div>
  );

  return (
    <div className={cn("w-full space-y-4", className)}>
      {searchBoxPosition === "top" && searchBox}

      <div className={cn(
        layout === "grid"
          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          : "space-y-4"
      )}>
        {filters.map((filter) => {
          const commonProps = {
            key: filter.id,
            label: filter.label,
            colorVariant: filter.colorVariant,
            placeholder: filter.placeholder || `Select ${filter.label.toLowerCase()}...`,
            searchPlaceholder: filter.searchPlaceholder || `Search ${filter.label.toLowerCase()}...`,
            options: filter.options,
            onSearch: filter.onSearch,
            renderOption: filter.renderOption,
            renderSelected: filter.renderSelected,
            "data-testid": `filter-${filter.id}`,
          };

          if (filter.multiple) {
            return (
              <KiiAutoSuggestFilter
                {...commonProps}
                multiple={true}
                value={(filterValues[filter.id] as string[]) || []}
                onValueChange={(values: string[]) => handleFilterChange(filter.id, values)}
              />
            );
          }

          return (
            <KiiAutoSuggestFilter
              {...commonProps}
              multiple={false}
              value={(filterValues[filter.id] as string | null) ?? null}
              onValueChange={(value: string | null) => handleFilterChange(filter.id, value)}
            />
          );
        })}
      </div>

      {searchBoxPosition === "bottom" && searchBox}
    </div>
  );
}
